import React, { useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Avatar, TextField, MenuItem, Tabs, Tab,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { getEditorAndProjectList } from '../../apis/apiList/editorPaymentApi';
import { useApp } from '../../context/AppContext';
import ClipLoader from 'react-spinners/ClipLoader';
import theme from '../../theme/theme';
import EditorPaymentList from '../../components/editorPayment/EditorPaymentList';

const override = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgb(12 5 5 / 5%)",
  backdropFilter: "blur(5px)",
  zIndex: 999,
};

const timeFilterOptions = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Last Year', value: 'lastYear' },
  { label: 'All Time', value: 'allTime' },
];

const paymentOptions = ['Paid', 'Unpaid'];

const EditorPaymentDetail = () => {
  const [projects, setProjects] = useState([]);
  const [editorDetail, setEditorDetail] = useState(null);
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [totalAmountUnpaid, setTotalAmountUnpaid] = useState(0);
  const [loading, setLoading] = useState(true);
  // const { editorId } = useParams();
  const { userDetails } = useApp();
  const [value, setValue] = useState(0);

  const [filters, setFilters] = useState({
    time: 'thisMonth',
    payment: ''
  });

  const fetchEditorDetails = async (time, payment) => {
    setLoading(true);
    try {
      const response = await getEditorAndProjectList(userDetails._id, {
        params: { time, payment },
        header: {
          'Content-Type': 'application/json',
          'x-access-token': userDetails.jwtToken,
        },
      });

      setEditorDetail(response.editorDetail);
      setProjects(response.projects);
      setTotalAmountPaid(response?.totalPaid || 0);
      setTotalAmountUnpaid(response?.totalUnpaid || 0);
    } catch (error) {
      console.error('Error fetching editor details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEditorDetails(filters.time, filters.payment);
  }, [userDetails._id]);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleTotalAmount = (payment) => {
    if (payment.isPaid) {
      setTotalAmountPaid(prev => prev + payment.amount);
      setTotalAmountUnpaid(prev => prev - payment.amount);
    } else {
      setTotalAmountUnpaid(prev => prev + payment.amount);
      setTotalAmountPaid(prev => prev - payment.amount);
    }
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
  };

  const handleFilterTotalAmount = (payment) => {
    setTotalAmountPaid(payment?.totalPaid || 0);
    setTotalAmountUnpaid(payment?.totalUnpaid || 0);
  };

  return (
    <>
      {loading ? (
        <Box sx={override}>
          <ClipLoader
            color={theme.palette.primary.main}
            loading={loading}
            size={150}
          />
        </Box>
      ) : (
        <Box mt={0}>
          <Box display="flex" justifyContent="space-between">
            <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mb={4} sx={{ width: '50%' }}>
              <Avatar
                src={editorDetail?.profilePicture}
                sx={{ width: 160, height: 160 }}
                alt={editorDetail?.firstName}
              />
              <Box>
                <Stack direction="column" gap={2}>
                  <Stack direction="row" gap={3} alignItems="center">
                    <Typography variant="h2" component="h5">
                      {editorDetail?.firstName} {editorDetail?.lastName}
                    </Typography>
                  </Stack>
                  <Typography>Job Title: {editorDetail?.jobTitle}</Typography>
                  <Typography>Email: {editorDetail?.email}</Typography>
                  <Typography>Phone: {editorDetail?.phone}</Typography>
                </Stack>
              </Box>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'end',
                gap: 1,
                width: '50%',
                justifyContent: 'end',
                mr: '20px',
              }}
            >
              <Typography variant="h4">
                Total Amount Paid: <strong>${totalAmountPaid}</strong>
              </Typography>
              <Typography variant="h4">
                Total Amount Pending: <strong>${totalAmountUnpaid}</strong>
              </Typography>
            </Box>
          </Box>

          {/* Tabs */}
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleTabChange} aria-label="editor tabs" sx={{ minHeight: '35px' }}>
                <Tab
                  label="Project payments"
                  sx={{
                    fontSize: '17px',
                    textTransform: 'capitalize',
                    paddingLeft: 0,
                    paddingRight: '20px',
                    borderRight: '1px solid #CCC',
                  }}
                />
              </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
              {/* Filters */}
              <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                <TextField
                  select
                  label="Time Filter"
                  value={filters.time}
                  onChange={(e) => handleFilterChange('time', e.target.value)}
                  size="small"
                  sx={{ width: '25%' }}
                >
                  {timeFilterOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Payment Filter"
                  value={filters.payment}
                  onChange={(e) => handleFilterChange('payment', e.target.value)}
                  size="small"
                  sx={{ width: '25%' }}
                >
                  <MenuItem value="">All</MenuItem>
                  {paymentOptions.map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <EditorPaymentList
                filters={filters}
                editorId={userDetails._id}
                userDetails={userDetails}
                handleTotalAmount={handleTotalAmount}
                handleFilterTotalAmount={handleFilterTotalAmount}
              />
            </CustomTabPanel>
          </Box>
        </Box>
      )}
    </>
  );
};

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box py={3}>{children}</Box>}
    </div>
  );
}

export default EditorPaymentDetail;
